import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Modal, Card, Badge, ListGroup } from 'react-bootstrap';
import { 
  FaUser, FaGraduationCap, FaCalendar, FaBook, 
  FaBuilding, FaCheckCircle, FaExclamationTriangle,
  FaClipboardList, FaUserCheck, FaLayerGroup
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import {
  useColleges,
  useDegrees,
  usePrograms,
  useAcademicYears,
  useProgramYears,
  useSemesters,
  useRegistrationTypes,
  useExamAnnouncements,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SemesterRegistration = () => {
  const [searchStudentId, setSearchStudentId] = useState("");
  const [eligibilityData, setEligibilityData] = useState(null);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showCourseSelectionModal, setShowCourseSelectionModal] = useState(false);
  const [electiveGroups, setElectiveGroups] = useState([]);

  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { programmeYears: programYears, loading: programYearsLoading } = useProgramYears();
  const { semesters, loading: semestersLoading } = useSemesters();
  const { registrationTypes, loading: registrationTypesLoading } = useRegistrationTypes();
  const { examAnnouncements, loading: examAnnouncementsLoading } = useExamAnnouncements();

  const dropdownsLoading = collegesLoading || degreesLoading || programsLoading ||
                           academicYearsLoading || programYearsLoading || semestersLoading || registrationTypesLoading || examAnnouncementsLoading;

  const checkEligibility = async (studentId, academicYear, programmeYear, semester, programmes) => {
    try {
      const token = Cookies.get('token');
      const payload = {
        academicYear: Number(academicYear),
        programmeYear: Number(programmeYear),
        semester: Number(semester),
        programmes: Number(programmes),
        studentId: studentId
      };

      const response = await axios.post(
        `${BASE_URL}/academic/semesterRegistrationEligibility`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        setEligibilityData(response.data);
        setShowEligibilityModal(true);
        
        if (response.data.eligible) {
          toast.success("Student is eligible for semester registration!");
          if (response.data.courses) {
            setAvailableCourses(response.data.courses);
          }
        } else {
          toast.warning("Student is not eligible for semester registration.");
        }
        return response.data.eligible;
      }
    } catch (error) {
      console.error("Failed to check eligibility:", error);
      toast.error("Failed to check registration eligibility.");
      return false;
    }
  };

  const customFetch = async () => {
    const token = Cookies.get('token');
    try {
      const res = await axios.get(`${BASE_URL}/academic/getAllSemesterRegistrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.registrations || [];
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
      return [];
    }
  };

  const handleRegistrationWithCourses = async (form) => {
    if (selectedCourses.length === 0) {
      toast.error("Please select at least one course for registration.");
      return false;
    }

    try {
      const token = Cookies.get('token');
      const payload = {
        academicYear: Number(form.academicYear),
        programmeYear: Number(form.programmeYear),
        programmes: Number(form.programmes),
        semester: Number(form.semester),
        colleges: Number(form.colleges),
        degree: Number(form.degree),
        examAnnouncement: Number(form.examAnnouncement),
        registrationType: Number(form.registrationType),
        studentId: form.studentId,
        courselist: selectedCourses.map(c => Number(c))
      };

      const response = await axios.post(
        `${BASE_URL}/academic/createSemesterRegistrationByCourseList`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        toast.success("Semester registration with courses created successfully!");
        setSelectedCourses([]);
        setShowCourseSelectionModal(false);
        return true;
      }
    } catch (error) {
      console.error("Failed to create registration with courses:", error);
      toast.error("Failed to create semester registration with courses.");
      return false;
    }
  };

  const formFields = [
    {
      title: 'Student & Academic Information',
      fields: [
        { 
          name: 'studentId', 
          label: 'Student ID', 
          icon: FaUser, 
          required: true,
          placeholder: 'e.g., CS24 0001'
        },
        { 
          name: 'colleges', 
          label: 'College', 
          icon: FaBuilding, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(colleges) ? colleges : []).filter(Boolean).map(c => ({ value: c.id, label: c.exName })) 
        },
        { 
          name: 'degree', 
          label: 'Degree', 
          icon: FaGraduationCap, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(degrees) ? degrees : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })) 
        },
        { 
          name: 'programmes', 
          label: 'Program', 
          icon: FaBook, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(programs) ? programs : []).filter(Boolean).map(p => ({ value: p.id, label: p.exName })) 
        },
      ]
    },
    {
      title: 'Semester & Year Details',
      fields: [
        { 
          name: 'academicYear', 
          label: 'Academic Year', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(academicYears) ? academicYears : []).filter(Boolean).map(a => ({ value: a.id, label: a.exYear })) 
        },
        { 
          name: 'programmeYear', 
          label: 'Program Year', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(programYears) ? programYears : []).filter(Boolean).map(p => ({ value: p.id, label: p.exName })) 
        },
        { 
          name: 'semester', 
          label: 'Semester', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(semesters) ? semesters : []).filter(Boolean).map(s => ({ value: s.id, label: s.exName })) 
        },
      ]
    },
    {
      title: 'Registration Details',
      fields: [
        { 
          name: 'registrationType', 
          label: 'Registration Type', 
          icon: FaClipboardList, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(registrationTypes) ? registrationTypes : []).filter(Boolean).map(rt => ({ value: rt.id, label: rt.exName })) 
        },
        { 
          name: 'examAnnouncement', 
          label: 'Exam Announcement', 
          icon: FaClipboardList, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(examAnnouncements) ? examAnnouncements : []).filter(Boolean).map(ea => ({ value: ea.id, label: ea.exName })) 
        },
        { 
          name: 'electiveGroup', 
          label: 'Elective Group', 
          icon: FaLayerGroup, 
          as: 'select', 
          options: [
            { value: '', label: 'None' },
            ...electiveGroups.map(eg => ({ value: eg.id, label: eg.name }))
          ] 
        },
      ]
    }
  ];

  const validateForm = (form) => {
    const errors = {};
    if (!form.studentId?.trim()) errors.studentId = 'Student ID is required';
    if (!form.colleges) errors.colleges = 'College is required';
    if (!form.degree) errors.degree = 'Degree is required';
    if (!form.programmes) errors.programmes = 'Program is required';
    if (!form.academicYear) errors.academicYear = 'Academic Year is required';
    if (!form.programmeYear) errors.programmeYear = 'Program Year is required';
    if (!form.semester) errors.semester = 'Semester is required';
    if (!form.registrationType) errors.registrationType = 'Registration Type is required';
    if (!form.examAnnouncement) errors.examAnnouncement = 'Exam Announcement is required';
    
    return errors;
  };

  const transformPayload = (form, editId, userId) => {
    const payload = {
      academicYear: Number(form.academicYear),
      programmeYear: Number(form.programmeYear),
      programmes: Number(form.programmes),
      semester: Number(form.semester),
      colleges: Number(form.colleges),
      degree: Number(form.degree),
      examAnnouncement: Number(form.examAnnouncement),
      registrationType: Number(form.registrationType),
      studentId: form.studentId.trim(),
      electiveGroup: form.electiveGroup || ""
    };

    if (editId) {
      payload.id = editId;
    }

    return payload;
  };

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getAllSemesterRegistrations',
      save: '/academic/createSemesterRegistration',
      update: '/academic/updateSemesterRegistration',
      delete: '/academic/deleteSemesterRegistration'
    },
    validateForm: validateForm,
    initialFormState: {
      studentId: "",
      colleges: "",
      degree: "",
      programmes: "",
      academicYear: "",
      programmeYear: "",
      semester: "",
      registrationType: "",
      examAnnouncement: "",
      electiveGroup: ""
    },
    entityName: 'Semester Registration',
    fetchMethod: 'CUSTOM',
    customFetch: customFetch,
    onBeforeCreate: async (form) => {
      const isEligible = await checkEligibility(
        form.studentId,
        form.academicYear,
        form.programmeYear,
        form.semester,
        form.programmes
      );
      return isEligible;
    }
  };

  const transformToForm = (item) => {
    if (!item) return config.initialFormState;
    return {
      ...config.initialFormState,
      ...item,
      colleges: item.collegesId?.toString() || item.colleges?.toString() || "",
      degree: item.degreeId?.toString() || item.degree?.toString() || "",
      programmes: item.programmesId?.toString() || item.programmes?.toString() || "",
      academicYear: item.academicYearId?.toString() || item.academicYear?.toString() || "",
      programmeYear: item.programmeYearId?.toString() || item.programmeYear?.toString() || "",
      semester: item.semesterId?.toString() || item.semester?.toString() || "",
      registrationType: item.registrationTypeId?.toString() || item.registrationType?.toString() || "",
      examAnnouncement: item.examAnnouncementId?.toString() || item.examAnnouncement?.toString() || "",
      electiveGroup: item.electiveGroup || ""
    };
  };

  const tableColumns = [
    { key: 'studentId', label: 'Student ID', sortable: true },
    { key: 'studentName', label: 'Student Name', sortable: true, render: (item) => item.studentName || '-' },
    { key: 'programmeName', label: 'Program', sortable: true, render: (item) => item.programmeName || '-' },
    { key: 'semesterName', label: 'Semester', sortable: true, render: (item) => item.semesterName || '-' },
    { key: 'registrationTypeName', label: 'Registration Type', sortable: true, render: (item) => item.registrationTypeName || '-' },
    { 
      key: 'status', 
      label: 'Status', 
      sortable: true, 
      render: (item) => (
        <Badge bg={item.status === 'completed' ? 'success' : item.status === 'pending' ? 'warning' : 'secondary'}>
          {item.status || 'Pending'}
        </Badge>
      )
    },
  ];

  const csvHeaders = [
    { key: 'studentId', label: 'Student ID' },
    { key: 'studentName', label: 'Student Name' },
    { key: 'collegeName', label: 'College' },
    { key: 'degreeName', label: 'Degree' },
    { key: 'programmeName', label: 'Program' },
    { key: 'academicYearName', label: 'Academic Year' },
    { key: 'programmeYearName', label: 'Program Year' },
    { key: 'semesterName', label: 'Semester' },
    { key: 'registrationTypeName', label: 'Registration Type' },
    { key: 'examAnnouncementName', label: 'Exam Announcement' },
    { key: 'status', label: 'Status' }
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    return [
      { label: 'Student ID', value: item.studentId, icon: FaUser },
      { label: 'Student Name', value: item.studentName, icon: FaUser },
      { label: 'College', value: item.collegeName, icon: FaBuilding },
      { label: 'Degree', value: item.degreeName, icon: FaGraduationCap },
      { label: 'Program', value: item.programmeName, icon: FaBook },
      { label: 'Academic Year', value: item.academicYearName, icon: FaCalendar },
      { label: 'Program Year', value: item.programmeYearName, icon: FaCalendar },
      { label: 'Semester', value: item.semesterName, icon: FaCalendar },
      { label: 'Registration Type', value: item.registrationTypeName, icon: FaClipboardList },
      { label: 'Exam Announcement', value: item.examAnnouncementName, icon: FaClipboardList },
      { label: 'Elective Group', value: item.electiveGroup || 'None', icon: FaLayerGroup },
      { label: 'Status', value: item.status, icon: FaCheckCircle },
    ];
  };

  const handleCheckEligibility = async () => {
    if (!searchStudentId) {
      toast.error("Please enter a Student ID to check eligibility.");
      return;
    }

    const tempForm = config.initialFormState;
    tempForm.studentId = searchStudentId;

    toast.info("Please fill in Academic Year, Program Year, Semester, and Program fields to check eligibility.");
  };

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          <GenericCrudPage
            title="Semester Registration"
            pluralTitle="Semester Registrations"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaClipboardList}
            csvHeaders={csvHeaders}
            csvFilename="semester_registrations.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
          />

          <Modal show={showEligibilityModal} onHide={() => setShowEligibilityModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                <FaUserCheck className="me-2" />
                Eligibility Check Result
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {eligibilityData && (
                <Card>
                  <Card.Body>
                    <Alert variant={eligibilityData.eligible ? 'success' : 'warning'}>
                      {eligibilityData.eligible ? (
                        <>
                          <FaCheckCircle className="me-2" />
                          Student is eligible for semester registration
                        </>
                      ) : (
                        <>
                          <FaExclamationTriangle className="me-2" />
                          Student is not eligible for semester registration
                        </>
                      )}
                    </Alert>
                    {eligibilityData.message && (
                      <p className="mb-0">{eligibilityData.message}</p>
                    )}
                    {eligibilityData.reasons && (
                      <div className="mt-3">
                        <h6>Reasons:</h6>
                        <ul>
                          {eligibilityData.reasons.map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEligibilityModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showCourseSelectionModal} onHide={() => setShowCourseSelectionModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                <FaBook className="me-2" />
                Select Courses for Registration
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <ListGroup>
                {availableCourses.map((course) => (
                  <ListGroup.Item
                    key={course.id}
                    action
                    active={selectedCourses.includes(course.id)}
                    onClick={() => {
                      if (selectedCourses.includes(course.id)) {
                        setSelectedCourses(selectedCourses.filter(id => id !== course.id));
                      } else {
                        setSelectedCourses([...selectedCourses, course.id]);
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{course.code}</strong> - {course.name}
                        <br />
                        <small className="text-muted">Credits: {course.credits || 'N/A'}</small>
                      </div>
                      {selectedCourses.includes(course.id) && (
                        <FaCheckCircle className="text-success" />
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {availableCourses.length === 0 && (
                <Alert variant="info">No courses available for selection.</Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCourseSelectionModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setShowCourseSelectionModal(false)}>
                Confirm Selection ({selectedCourses.length} courses)
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default SemesterRegistration;